import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import styles from "./ViewDetails.module.scss";
import classNames from "classnames/bind";
import InvoiceInformation, {
  IInvoiceInformation,
} from "./component/invoice-information/InvoiceInformation";
import InvoiceDetails, {
  IInvoiceInInvoiceDetails,
} from "./component/invoice-details/InvoiceDetails";
import { useEffect, useState } from "react";
import { IInvoiceDetailsJobDetails, InvoiceDetailsAction } from "~/utils/interface/jobDetails";
import jobDetailsApi from "~/apis/jobDetails";
import { downloadBlob, handleApiError, notify } from "~/utils/helper";
import BaseModalV2 from "~/components/common/base-modal-v2/BaseModalV2";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import InvoicePayment, {
  IInvoicePayment,
} from "./component/invoice-payment/InvoicePayment";
import InvoiceSections, {
  ISectionsTableInInvoiceSections,
} from "./component/invoice-sections/InvoiceSections";
import InvoiceActivity, {
  IInvoiceActivity,
} from "./component/invoice-activity/InvoiceActivity";
import EmailInvoiceModal from "./component/email-invoice-modal/EmailInvoiceModal";
import RequestPaymentModal from "../main-content/tabs/request-payment-modal/RequestPaymentModal";
import ProcessPaymentModal from "../main-content/tabs/process-payment-modal/ProcessPaymentModal";

const cx = classNames.bind(styles);

interface ViewDetailsProps {
  jobId: string;
  invoiceId: string;
  onAddNote?: () => void;
  // FB-25/27: what to open on arrival (preview right after create, email from the list menu)
  initialAction?: InvoiceDetailsAction;
  onEdit?: (invoiceId: string) => void;
  onDeleted?: () => void;
}

const ViewDetails = ({ jobId, invoiceId, onAddNote, initialAction, onEdit, onDeleted }: ViewDetailsProps) => {
  const [invoiceDetails, setInvoiceDetails] =
    useState<IInvoiceDetailsJobDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [invoiceInformation, setInvoiceInformation] =
    useState<IInvoiceInformation | null>(null);
  const [invoice, setInvoice] = useState<IInvoiceInInvoiceDetails | null>(null);
  const [sectionsTable, setSectionsTable] = useState<
    ISectionsTableInInvoiceSections[] | null
  >(null);
  const [invoicePayment, setInvoicePayment] = useState<
    IInvoicePayment[] | null
  >(null);
  const [invoiceActivity, setInvoiceActivity] = useState<
    IInvoiceActivity[] | null
  >(null);
  const [isOpenEmailInvoiceModal, setIsOpenEmailInvoiceModal] = useState(initialAction === "email");
  const [isOpenPreview, setIsOpenPreview] = useState(initialAction === "preview");
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isOpenRequestPaymentModal, setIsOpenRequestPaymentModal] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [isOpenProcessPaymentModal, setIsOpenProcessPaymentModal] = useState(false);

  const fetchInvoiceDetails = async () => {
    try {
      setIsLoading(true);
      const response = await jobDetailsApi.getInvoiceDetailsJobDetails(
        jobId,
        invoiceId
      );
      setInvoiceDetails(response.data?.data);
      setIsLoading(false);
    } catch (error) {
      handleApiError(error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceDetails();
  }, [jobId, invoiceId, reloadTrigger]);

  const handleReloadInvoiceDetails = () => {
    setReloadTrigger((prev) => prev + 1);
  };

  const handleOpenEmailInvoiceModal = () => {
    setIsOpenPreview(false);
    setIsOpenEmailInvoiceModal(true);
  };

  const handleDownload = async () => {
    try {
      const response = await jobDetailsApi.downloadInvoicePdf(jobId, invoiceId);
      downloadBlob(response.data, `Invoice-${invoiceDetails?.invoiceUniqueCode || invoiceId}.pdf`);
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to download invoice");
    }
  };

  const handleDelete = async () => {
    try {
      await jobDetailsApi.deleteInvoice(jobId, invoiceId);
      notify("success", "Invoice deleted successfully");
      setIsOpenDelete(false);
      onDeleted?.();
    } catch (error: any) {
      setIsOpenDelete(false);
      notify("error", error?.response?.data?.message || "Failed to delete invoice");
    }
  };

  const canEdit = !!onEdit && !["Cancelled", "Void"].includes(invoiceDetails?.status || "");
  const recordActions = (
    <>
      <PrimaryButton title="Preview" onClick={() => setIsOpenPreview(true)} className={cx("email-invoice-button")} />
      <PrimaryButton title="Download" onClick={handleDownload} className={cx("email-invoice-button")} />
      {canEdit && (
        <PrimaryButton title="Edit" onClick={() => onEdit?.(invoiceId)} className={cx("email-invoice-button")} />
      )}
      <PrimaryButton title="Delete" onClick={() => setIsOpenDelete(true)} className={cx("email-invoice-button", "delete-button")} />
    </>
  );

  useEffect(() => {
    if (invoiceDetails) {
      const invoiceInformation: IInvoiceInformation = {
        number: invoiceDetails.invoiceUniqueCode || "",
        total: invoiceDetails.total || "",
        paid: invoiceDetails.paid || "",
        remaining: invoiceDetails.remaining?.toString() || "",
        page: invoiceDetails.page?.toString() || "",
        invoiceDate: invoiceDetails.invoiceDate || "",
        dueDate: invoiceDetails.dueDate || "",
        related:
          `${invoiceDetails.related?.contactName} (contact), ${invoiceDetails.related?.jobUniqueCode} (job)` ||
          "",
        status: invoiceDetails.status || "",
        signed: invoiceDetails.eSigned || "",
        isArchived: invoiceDetails.isArchived || "",
        createdBy: invoiceDetails.createdBy?.name || "",
        createdAt: invoiceDetails.createdAt || "",
      };

      const invoicePaymentsInDetails =
        invoiceDetails.invoicePayment?.map((payment) => ({
          paymentDate: payment.paymentDate,
          reference: payment.reference,
          amount: payment.paymentAmount,
          method: payment.paymentMethod,
          status: payment.status,
        })) || [];

      const invoice: IInvoiceInInvoiceDetails = {
        customer: {
          name: invoiceDetails.customer?.name || "N/A",
          address: invoiceDetails.customer?.address || "N/A",
          phone: invoiceDetails.customer?.phone || "N/A",
          email: invoiceDetails.customer?.email || "N/A",
        },
        secondaryBillingAddress: invoiceDetails.secondaryBillingAddress
          ? {
            name: invoiceDetails.customer?.name || "N/A",
            address: invoiceDetails.secondaryBillingAddress,
          }
          : undefined,

        salesRepresentative: {
          name: invoiceDetails.salesRepresentative?.name || "N/A",
          email: invoiceDetails.salesRepresentative?.email || "N/A",
          phone: invoiceDetails.salesRepresentative?.phone || "N/A",
        },
        insuranceCompany: {
          name: invoiceDetails.insuranceCompany?.name || "N/A",
          number: invoiceDetails.insuranceCompany?.number || "N/A",
          email: invoiceDetails.insuranceCompany?.email || "N/A",
        },
        invoiceTable: {
          invoice: invoiceDetails.invoiceUniqueCode || "N/A",
          date: invoiceDetails.invoiceDate || "N/A",
          // "Amount Due" = remaining balance (was the invoice total, still $X after a full payment)
          amountDue: invoiceDetails.amountDue != null ? Number(invoiceDetails.amountDue).toFixed(2) : "N/A",
          dueDate: invoiceDetails.dueDate || "N/A",
        },

        sectionsTable: invoiceDetails.sections.map((section) => ({
          title: section.sectionTitle,
          lines: section.lines.map((line) => ({
            name: line.itemName,
            quantity: Number(line.quantity),
            price: Number(line.price),
            amount: Number(line.quantity * line.price),
          })),
        })),

        note: invoiceDetails.customerNotes || "N/A",
        subTotalTable: {
          // invoices saved before tax support have sub_total = 0 -> fall back to total
          subTotal: (Number(invoiceDetails.subTotal) || Number(invoiceDetails.total || 0) - Number(invoiceDetails.taxAmount || 0)).toFixed(2),
          tax: Number(invoiceDetails.taxAmount || 0).toFixed(2),
          taxRate: Number(invoiceDetails.taxRate || 0),
          total: invoiceDetails.total || "N/A",
          amountPaid: invoiceDetails.paid || "N/A",
          balanceDue: invoiceDetails.amountDue != null ? Number(invoiceDetails.amountDue).toFixed(2) : "N/A",
        },
        invoicePayments: invoicePaymentsInDetails,
      };

      const sectionsTable: ISectionsTableInInvoiceSections[] =
        invoiceDetails.sections.map((section) => ({
          title: section.sectionTitle,
          lines: section.lines.map((line) => ({
            name: line.itemName,
            description: line.description,
            uom: line.uom,
            quantity: Number(line.quantity),
            type: line.type as "Material" | "Labor",
            cost: "Null",
            markup: Number(line.markup),
            price: Number(line.price),
            tax: "-",
            amount: Number(
              ((line.quantity * line.price) / 100) * line.markup +
              line.quantity * line.price
            ),
          })),
        }));

      const invoicePayment: IInvoicePayment[] =
        invoicePaymentsInDetails.map((payment) => ({
          paymentDate: payment.paymentDate,
          reference: payment.reference,
          amount: payment.amount,
          method: payment.method,
          status: payment.status,
        }));

      const invoiceActivity: IInvoiceActivity[] =
        invoiceDetails.activityLogs.map((activity) => ({
          postedOnDate: activity.createdAt || "",
          postedBy: activity.createdBy?.name || "",
          text: activity.description || "",
        }));
      setInvoiceInformation(invoiceInformation);
      setInvoice(invoice);
      setSectionsTable(sectionsTable);
      setInvoicePayment(invoicePayment);
      setInvoiceActivity(invoiceActivity);
    }
  }, [invoiceDetails]);

  return (
    <div className={cx("view-details-container")}>
      <div className={cx("header")}>
        <h3 className={cx("title")}>View Details</h3>

        <div className={cx("actions")}>
          {recordActions}
          <PrimaryButton
            title="Email invoice"
            onClick={handleOpenEmailInvoiceModal}
            className={cx("email-invoice-button")}
          />
          <PrimaryButton
            title="Text Payment Request"
            onClick={() => setIsOpenRequestPaymentModal(true)}
            className={cx("request-payment-button")}
          />
        </div>
      </div>

      {isLoading ? (
        <div className={cx("loading-container")}>
          <LoadingMainContent />
        </div>
      ) : (
        <div className={cx("body")}>
          <div className={cx("section-container")}>
            <InvoiceInformation invoiceInformation={invoiceInformation} />
          </div>
          <div className={cx("section-container")}>
            <InvoiceDetails
              invoice={invoice}
              onMakePayment={() => setIsOpenProcessPaymentModal(true)}
            />
          </div>

          <div className={cx("section-container")}>
            <InvoiceSections sectionsTable={sectionsTable} />
          </div>

          <div className={cx("section-container", "!pt-0")}>
            <InvoicePayment
              invoicePayment={invoicePayment}
              idJob={jobId}
              onPaymentSuccess={handleReloadInvoiceDetails}
              invoiceId={invoiceId}
              onProcessPaymentNow={() => setIsOpenProcessPaymentModal(true)}
            />
          </div>

          <div className={cx("section-container", "!pt-0")}>
            <InvoiceActivity
              invoiceActivity={invoiceActivity}
              onAddNote={onAddNote}
            />
          </div>
        </div>
      )}

      <EmailInvoiceModal
        isOpen={isOpenEmailInvoiceModal}
        onClose={() => setIsOpenEmailInvoiceModal(false)}
        customer={invoiceDetails?.customer}
        invoiceId={invoiceId}
      />

      {/* FB-27: the customer-facing page (same view the emailed link opens), staff mode by numeric id */}
      <BaseModalV2
        open={isOpenPreview}
        onClose={() => setIsOpenPreview(false)}
        title={`Preview ${invoiceDetails?.invoiceUniqueCode || "Invoice"}`}
        width="1100px"
        maxWidth="95vw"
      >
        <iframe
          title="Invoice preview"
          src={`/invoices/${invoiceId}/view`}
          className={cx("preview-frame")}
        />
        <div className={cx("preview-actions")}>
          <PrimaryButton title="Email invoice" onClick={handleOpenEmailInvoiceModal} />
          {recordActions}
        </div>
      </BaseModalV2>

      <ModalAlertDelete
        open={isOpenDelete}
        description="Are you sure you want to delete this invoice?"
        onCancel={() => setIsOpenDelete(false)}
        onConfirm={handleDelete}
      />

      <RequestPaymentModal
        open={isOpenRequestPaymentModal}
        onClose={() => setIsOpenRequestPaymentModal(false)}
        jobId={jobId}
        onSuccess={handleReloadInvoiceDetails}
      />

      <ProcessPaymentModal
        open={isOpenProcessPaymentModal}
        onClose={() => setIsOpenProcessPaymentModal(false)}
        jobId={jobId}
        invoiceId={invoiceId}
      />
    </div>
  );
};

export default ViewDetails;
